import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as L from 'leaflet';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { AttendanceZone, AttendanceZoneCreatePayload, AttendanceZoneUpdatePayload } from './zone.interface';

type ZoneWorkspaceMode = 'view' | 'create' | 'edit';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface ZoneFormValue {
  companiesRecordId: string;
  name: string;
  icon: string;
  lat: number | '';
  lng: number | '';
  radiusMeters: number | '';
  isActive: boolean;
}

@Component({
  selector: 'app-zone-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './zone-detail.component.html',
  styleUrl: './zone-detail.component.scss',
})
export class ZoneDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly ICON_MAP: Record<string, string> = {
    briefcase: 'work',
    storefront: 'storefront',
    apartment: 'apartment',
    factory: 'factory',
    warehouse: 'warehouse',
    place: 'place',
    pin_drop: 'pin_drop',
  };

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  @ViewChild('mapContainer') private mapContainer?: ElementRef<HTMLDivElement>;

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: ZoneWorkspaceMode = 'view';
  companies: CompaniesRecord[] = [];
  selectedZoneId: string | null = null;
  selectedZone: AttendanceZone | null = null;
  form: ZoneFormValue = this.createEmptyForm();
  mapStyle: 'street' | 'satellite' = 'street';

  readonly readAccess = { resource: 'zones', action: 'read' } as const;
  readonly createAccess = { resource: 'zones', action: 'create' } as const;
  readonly updateAccess = { resource: 'zones', action: 'update' } as const;
  readonly deleteAccess = { resource: 'zones', action: 'delete' } as const;

  readonly zoneIcons = ['briefcase', 'storefront', 'apartment', 'factory', 'warehouse', 'place', 'pin_drop'];

  private map: L.Map | null = null;
  private tileLayer: L.TileLayer | null = null;
  private centerMarker: L.Marker | null = null;
  private zoneCircle: L.Circle | null = null;
  private readonly defaultCenter: L.LatLngTuple = [21.1619, -86.8515];
  private syncingMapFromForm = false;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.loadBaseData().then(() => this.syncWithRoute());
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.renderMap(), 0);
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get hasMultipleCompanies(): boolean {
    return this.companies.length > 1;
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('zonesCatalog.detail.createTitle');
    }
    if (this.isEditMode) {
      return this.t('zonesCatalog.detail.editTitle');
    }
    return this.selectedZone?.name || this.t('zonesCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('zonesCatalog.detail.createDescription');
    }
    if (this.isEditMode) {
      return this.t('zonesCatalog.detail.editDescription');
    }
    return this.t('zonesCatalog.detail.description');
  }

  get mapCoordinatesLabel(): string {
    if (this.form.lat === '' || this.form.lng === '') {
      return this.t('zonesCatalog.labels.pendingCoordinates');
    }
    return `${Number(this.form.lat).toFixed(6)}, ${Number(this.form.lng).toFixed(6)}`;
  }

  get googleMapsUrl(): string {
    const lat = this.selectedZone?.lat ?? this.form.lat;
    const lng = this.selectedZone?.lng ?? this.form.lng;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }

  get selectedIconLabel(): string {
    return this.getZoneIconLabel(this.form.icon);
  }

  get selectedMaterialIcon(): string {
    return this.getMaterialIconName(this.form.icon);
  }

  onRadiusChange(): void {
    if (this.form.radiusMeters === '') {
      return;
    }
    this.form.radiusMeters = Math.max(10, Number(this.form.radiusMeters || 0));
    this.syncMapFromFormValues();
  }

  onCoordinateInput(): void {
    this.syncMapFromFormValues();
  }

  onIconChange(): void {
    this.syncMapFromFormValues();
  }

  setMapStyle(style: 'street' | 'satellite'): void {
    if (this.mapStyle === style && this.tileLayer) {
      return;
    }
    this.mapStyle = style;
    this.applyTileLayer();
  }

  async copyCoordinates(coordinates?: string): Promise<void> {
    if (!coordinates) {
      return;
    }
    try {
      await navigator.clipboard.writeText(coordinates);
      this.showNotification('snackbar-success', this.t('zonesCatalog.notifications.coordinatesCopied'), 'bottom', 'center');
    } catch {
      this.showNotification('snackbar-danger', this.t('zonesCatalog.notifications.copyError'), 'bottom', 'center');
    }
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/zones']);
  }

  startEdit(): void {
    if (!this.selectedZone || !this.hasAccess('update')) {
      return;
    }
    this.mode = 'edit';
    this.form = this.createFormFromZone(this.selectedZone);
    this.syncMapInteractivity();
    void this.router.navigate(['/attendance/zones', this.selectedZone.zoneId, 'edit']);
  }

  cancelEdit(): void {
    if (this.selectedZone) {
      this.mode = 'view';
      this.form = this.createFormFromZone(this.selectedZone);
      this.syncMapFromFormValues();
      this.syncMapInteractivity();
      void this.router.navigate(['/attendance/zones', this.selectedZone.zoneId]);
      return;
    }

    this.goBackToList();
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const createdZone = await firstValueFrom(
          this.apiService.post<AttendanceZone>('attendance/zones', payload)
        );
        this.showNotification('snackbar-success', this.t('zonesCatalog.notifications.created'), 'bottom', 'center');
        void this.router.navigate(['/attendance/zones', createdZone.zoneId]);
        return;
      }

      if (this.isEditMode && this.selectedZoneId) {
        const payload = this.buildUpdatePayload();
        await firstValueFrom(this.apiService.patch(`attendance/zones/${this.selectedZoneId}`, payload));
        this.showNotification('snackbar-success', this.t('zonesCatalog.notifications.updated'), 'bottom', 'center');
        void this.router.navigate(['/attendance/zones', this.selectedZoneId]);
      }
    } catch (error) {
      console.error('Error saving attendance zone', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('zonesCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedZone || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('zonesCatalog.dialog.deleteTitle'),
        message: this.t('zonesCatalog.dialog.deleteMessage', { zone: this.selectedZone.name }),
        confirmLabel: this.t('zonesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedZoneId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/zones/${this.selectedZoneId}`));
        this.showNotification('snackbar-success', this.t('zonesCatalog.notifications.deleted'), 'bottom', 'center');
        void this.router.navigate(['/attendance/zones']);
      } catch (error) {
        console.error('Error deleting attendance zone', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('zonesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  private async loadBaseData(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.normalizeCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading companies for attendance zones', error);
      this.companies = [];
    } finally {
      this.isLoading = false;
    }
  }

  private async syncWithRoute(): Promise<void> {
    const zoneId = this.route.snapshot.paramMap.get('zoneId');
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/zones/new');
    const isEditRoute = /\/attendance\/zones\/[^/]+\/edit$/.test(currentUrl);

    if (isCreateRoute) {
      this.enterCreateMode();
      return;
    }

    if (!zoneId) {
      void this.router.navigate(['/attendance/zones']);
      return;
    }

    this.isLoading = true;
    try {
      const zone = await firstValueFrom(this.apiService.get<AttendanceZone>(`attendance/zones/${zoneId}`));
      this.selectedZone = this.normalizeZone(zone);
      this.selectedZoneId = this.selectedZone.zoneId;
      this.form = this.createFormFromZone(this.selectedZone);
      this.mode = isEditRoute ? 'edit' : 'view';
      this.syncMapFromFormValues();
      this.syncMapInteractivity();
    } catch (error) {
      console.error('Error loading attendance zone detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('zonesCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      void this.router.navigate(['/attendance/zones']);
    } finally {
      this.isLoading = false;
      setTimeout(() => this.renderMap(), 0);
    }
  }

  private enterCreateMode(): void {
    this.mode = 'create';
    this.selectedZone = null;
    this.selectedZoneId = null;
    this.form = this.createEmptyForm();
    if (!this.hasMultipleCompanies && this.companies[0]?.companiesRecordId) {
      this.form.companiesRecordId = this.companies[0].companiesRecordId;
    }
    this.syncMapFromFormValues();
    this.syncMapInteractivity();
    setTimeout(() => this.renderMap(), 0);
  }

  private createEmptyForm(): ZoneFormValue {
    return {
      companiesRecordId: '',
      name: '',
      icon: 'briefcase',
      lat: '',
      lng: '',
      radiusMeters: 100,
      isActive: true,
    };
  }

  private createFormFromZone(zone: AttendanceZone): ZoneFormValue {
    return {
      companiesRecordId: zone.companiesRecordId,
      name: zone.name,
      icon: zone.icon || 'briefcase',
      lat: zone.lat,
      lng: zone.lng,
      radiusMeters: zone.radiusMeters,
      isActive: zone.isActive !== false,
    };
  }

  private buildCreatePayload(): AttendanceZoneCreatePayload {
    return {
      companiesRecordId: this.form.companiesRecordId,
      name: this.form.name.trim(),
      icon: this.form.icon.trim(),
      lat: Number(this.form.lat),
      lng: Number(this.form.lng),
      radiusMeters: Number(this.form.radiusMeters),
    };
  }

  private buildUpdatePayload(): AttendanceZoneUpdatePayload {
    return {
      name: this.form.name.trim(),
      icon: this.form.icon.trim(),
      lat: Number(this.form.lat),
      lng: Number(this.form.lng),
      radiusMeters: Number(this.form.radiusMeters),
      isActive: this.form.isActive,
    };
  }

  private validateForm(): string | null {
    if (!this.form.companiesRecordId) {
      return this.t('zonesCatalog.notifications.validationCompany');
    }
    if (!this.form.name.trim()) {
      return this.t('zonesCatalog.notifications.validationName');
    }
    if (this.form.lat === '' || this.form.lng === '') {
      return this.t('zonesCatalog.notifications.validationCoordinates');
    }
    if (this.form.radiusMeters === '' || Number(this.form.radiusMeters) < 10) {
      return this.t('zonesCatalog.notifications.validationRadius');
    }
    return null;
  }

  private renderMap(): void {
    const container = this.mapContainer?.nativeElement;
    if (!container) {
      return;
    }

    if (!this.map) {
      this.map = L.map(container, {
        zoomControl: true,
        attributionControl: true,
      }).setView(this.defaultCenter, 13);

      this.applyTileLayer();
      L.control.scale({ imperial: false, position: 'bottomleft' }).addTo(this.map);

      this.map.on('click', (event: L.LeafletMouseEvent) => {
        if (this.isViewMode) {
          return;
        }
        this.setCoordinates(event.latlng.lat, event.latlng.lng);
      });
    }

    this.ensureMapLayers();
    this.syncMapFromFormValues();
    this.syncMapInteractivity();
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private applyTileLayer(): void {
    if (!this.map) {
      return;
    }

    if (this.tileLayer) {
      this.map.removeLayer(this.tileLayer);
    }

    const satellite = this.mapStyle === 'satellite';
    this.tileLayer = L.tileLayer(
      satellite
        ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
        attribution: satellite
          ? 'Tiles &copy; Esri'
          : '&copy; OpenStreetMap contributors',
      }
    );
    this.tileLayer.addTo(this.map);
  }

  private ensureMapLayers(): void {
    if (!this.map) {
      return;
    }

    if (!this.centerMarker) {
      this.centerMarker = L.marker(this.defaultCenter, {
        draggable: !this.isViewMode,
        icon: this.buildMarkerIcon(this.form.icon),
      }).addTo(this.map);

      this.centerMarker.on('dragend', () => {
        const latLng = this.centerMarker?.getLatLng();
        if (!latLng || this.isViewMode) {
          return;
        }
        this.setCoordinates(latLng.lat, latLng.lng);
      });
    }

    if (!this.zoneCircle) {
      this.zoneCircle = L.circle(this.defaultCenter, {
        radius: Number(this.form.radiusMeters || 100),
        color: '#2563eb',
        weight: 2,
        opacity: 0.95,
        fillColor: '#60a5fa',
        fillOpacity: 0.22,
      }).addTo(this.map);
    }
  }

  private syncMapFromFormValues(): void {
    if (!this.map || !this.centerMarker || !this.zoneCircle) {
      return;
    }

    const hasCoordinates = this.form.lat !== '' && this.form.lng !== '';
    const lat = hasCoordinates ? Number(this.form.lat) : this.defaultCenter[0];
    const lng = hasCoordinates ? Number(this.form.lng) : this.defaultCenter[1];
    const radius = Number(this.form.radiusMeters || 100);

    this.syncingMapFromForm = true;
    const center = L.latLng(lat, lng);
    this.centerMarker.setIcon(this.buildMarkerIcon(this.form.icon));
    this.centerMarker.setLatLng(center);
    this.zoneCircle.setLatLng(center);
    this.zoneCircle.setRadius(radius);
    this.map.setView(center, hasCoordinates ? 16 : 13);
    this.syncingMapFromForm = false;
  }

  private syncMapInteractivity(): void {
    if (!this.centerMarker) {
      return;
    }

    if (this.isViewMode) {
      this.centerMarker.dragging?.disable();
    } else {
      this.centerMarker.dragging?.enable();
    }
  }

  private setCoordinates(lat: number, lng: number): void {
    if (this.syncingMapFromForm) {
      return;
    }
    this.form.lat = Number(lat.toFixed(6));
    this.form.lng = Number(lng.toFixed(6));
    this.syncMapFromFormValues();
  }

  private destroyMap(): void {
    this.centerMarker = null;
    this.zoneCircle = null;
    this.tileLayer = null;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private normalizeZone(zone: Partial<AttendanceZone>): AttendanceZone {
    const isActive = zone.isActive !== false;
    const lat = Number(zone.lat || 0);
    const lng = Number(zone.lng || 0);
    const radius = Number(zone.radiusMeters || 0);
    return {
      zoneId: zone.zoneId || crypto.randomUUID(),
      center: zone.center || '',
      companiesRecordId: zone.companiesRecordId || '',
      name: zone.name || '',
      icon: zone.icon || 'briefcase',
      type: 'circle',
      lat,
      lng,
      radiusMeters: radius,
      geohash: zone.geohash || '',
      isActive,
      companyName: this.companies.find((company) => company.companiesRecordId === zone.companiesRecordId)?.name || '-',
      iconLabel: this.getZoneIconLabel(zone.icon || 'briefcase'),
      typeLabel: this.t('zonesCatalog.types.circle'),
      radiusLabel: this.t('zonesCatalog.labels.radiusMeters', { value: radius }),
      coordinatesLabel: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private buildMarkerIcon(icon: string): L.DivIcon {
    const materialIcon = this.getMaterialIconName(icon);
    return L.divIcon({
      className: 'zone-map-marker',
      html: `<span class="material-icons">${materialIcon}</span>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
  }

  private getZoneIconLabel(icon: string): string {
    return this.t(`zonesCatalog.icons.${icon}`);
  }

  getMaterialIconName(icon: string): string {
    return ZoneDetailComponent.ICON_MAP[icon] || 'place';
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'zones', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
